import useSWR from 'swr';
import { useSelector } from 'react-redux';
import { fetchGet, PATHNAME, schema } from '../api/booked-tickets';
import { withManageConfig } from './utils';
import { emptyArray } from '../../misc';
import { setTickets } from '../../../modules/access-control/model/state';

// The logged-in user's manage JWT token from redux. We can't rely on
// mAnage.config.envJWTToken alone: that getter reads localStorage
// `${instance}.user`, which is only populated by the OCA login flow.
// Users logging in through any other flow (m-anage SSO, direct login, etc.)
// have their manage token in redux at login_info.manage_api_user.token, and
// without it the BookedTickets endpoint authenticates as no user and
// returns an empty array.
const selectManageUserToken = (state) => state?.user?.login_info?.manage_api_user?.token;

export const useBookedTickets = (params) => {
  const manageUserToken = useSelector(selectManageUserToken);
  const merged = withManageConfig(params);
  const withUserToken = manageUserToken
    ? { ...merged, envJWTToken: manageUserToken }
    : merged;
  const result = schema.safeParse(withUserToken);
  const key = result.success ? { PATHNAME, ...result.data } : null;
  const swr = useSWR(key, fetchGet(result.data), {
    revalidateOnFocus: false,
    dedupingInterval: 5 * 60 * 1000 // 5 min
    // NOTE: do NOT use SWR's `fallbackData: emptyArray` here. It causes
    // `isLoading` to be `false` and `data` to be `[]` on the very first
    // render, which lets gating callers (AccessControlWrapper /
    // AccessControlGuard) proceed with an empty user before the fetch
    // resolves. Instead we expose `data` defaulted to emptyArray below so
    // non-gating consumers still always get an array, while keeping
    // `isLoading` truthful for the gating ones.
  });
  // `isLoading` from SWR is `false` whenever the key is `null` (schema parse
  // failed because e.g. envJWTToken / eventId / hostApi aren't ready yet on
  // the first render). A naive `if (isLoading) wait` guard therefore falls
  // through and lets composeUser run with an empty array.
  //
  // `isReady` is the correct gating signal:
  //  - If there is NO manage user token in redux, we will never make this
  //    request (anonymous user), so we're immediately "ready".
  //  - Otherwise we must wait until the request actually resolves (data or
  //    error). We do NOT short-circuit on `key === null` because the key is
  //    transiently null on the first render while mAnage.config hydrates.
  const isReady = !manageUserToken || swr.data !== undefined || !!swr.error;
  if (isReady) setTickets(swr.data);
  return { ...swr, isReady, data: swr.data ?? emptyArray };
};
